/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { FreeDay } from '../models/FreeDay';
import type { FreeDayCreate } from '../models/FreeDayCreate';
import type { FreeDayPage } from '../models/FreeDayPage';
import type { FreeDayUpdate } from '../models/FreeDayUpdate';
import type { HasUsersWithoutEntry } from '../models/HasUsersWithoutEntry';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class FreeDaysService {

  constructor(public readonly httpRequest: BaseHttpRequest) {}

  /**
   * Listet FreeDays, optional gefiltert nach Jahr (query param year) und paginiert.
   * @returns FreeDayPage Liste der FreeDays
   * @throws ApiError
   */
  public getFreeDays({
    page,
    limit = 25,
    sort,
    year,
    search,
    filtered,
  }: {
    page?: number,
    limit?: number,
    sort?: string,
    year?: number,
    search?: string,
    filtered?: string,
  }): CancelablePromise<FreeDayPage> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/free-days',
      query: {
        'page': page,
        'limit': limit,
        'sort': sort,
        'year': year,
        'search': search,
        'filtered': filtered,
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
      },
    });
  }

  /**
   * Legt einen FreeDay an.
   * @returns FreeDay Der angelegte FreeDay
   * @throws ApiError
   */
  public createFreeDay({
    requestBody,
  }: {
    requestBody: FreeDayCreate,
  }): CancelablePromise<FreeDay> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/free-days',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        409: `Conflict (z.B. doppelte Intervalle)`,
        422: `Unprocessable Entity (Validierungsfehler)`,
      },
    });
  }

  /**
   * Holt einen FreeDay.
   * @returns FreeDay FreeDay
   * @throws ApiError
   */
  public getFreeDay({
    id,
  }: {
    id: string,
  }): CancelablePromise<FreeDay> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/free-days/{id}',
      path: {
        'id': id,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * Aktualisiert einen FreeDay vollständig.
   * @returns FreeDay Der aktualisierte FreeDay
   * @throws ApiError
   */
  public updateFreeDay({
    id,
    requestBody,
  }: {
    id: string,
    requestBody: FreeDayUpdate,
  }): CancelablePromise<FreeDay> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/free-days/{id}',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        409: `Conflict (z.B. doppelte Intervalle)`,
        422: `Unprocessable Entity (Validierungsfehler)`,
      },
    });
  }

  /**
   * Löscht einen FreeDay.
   * @returns void
   * @throws ApiError
   */
  public deleteFreeDay({
    id,
  }: {
    id: string,
  }): CancelablePromise<void> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/free-days/{id}',
      path: {
        'id': id,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * Prüft, ob es Benutzer gibt, die für den angegebenen FreeDay keinen Eintrag haben.
   * @returns HasUsersWithoutEntry Ergebnis der Prüfung
   * @throws ApiError
   */
  public hasUsersWithoutEntry({
    id,
  }: {
    id: string,
  }): CancelablePromise<HasUsersWithoutEntry> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/free-days/{id}/has-users-without-entry',
      path: {
        'id': id,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

}
